import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { HealthArticle } from '../types';
import { showToast } from '../utils/toast';

export function useArticles() {
  const [articles, setArticles] = useState<HealthArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('articles')
          .select(`
            id,
            title,
            excerpt,
            content,
            author_id,
            category,
            tags,
            read_time,
            featured,
            views,
            likes,
            cover_image,
            published_at,
            created_at,
            profiles(full_name)
          `)
          .order('published_at', { ascending: false });

        if (error) throw error;

        // Format the articles
        const formattedArticles: HealthArticle[] = data.map(article => ({
          id: article.id,
          title: article.title,
          excerpt: article.excerpt || '',
          content: article.content,
          author: article.profiles?.full_name || 'Unknown Author',
          authorId: article.author_id,
          category: article.category,
          tags: article.tags,
          readTime: article.read_time,
          publishDate: new Date(article.published_at).toISOString().split('T')[0],
          featured: article.featured,
          views: article.views,
          likes: article.likes,
          coverImage: article.cover_image
        }));

        setArticles(formattedArticles);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch articles'));
        showToast.system.error('Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const incrementViews = async (articleId: string) => {
    try {
      const { error } = await supabase.rpc('increment_article_views', { article_id: articleId });
      
      if (error) throw error;
      
      // Update local state
      setArticles(prev => prev.map(article => 
        article.id === articleId 
          ? { ...article, views: article.views + 1 } 
          : article
      ));
    } catch (err) {
      console.error('Error incrementing article views:', err);
    }
  };

  const likeArticle = async (articleId: string) => {
    try {
      // Update the likes count in the database
      const { error } = await supabase
        .from('articles')
        .update({ likes: supabase.rpc('increment', { row_id: articleId, table: 'articles', column: 'likes', amount: 1 }) })
        .eq('id', articleId);
      
      if (error) throw error;
      
      // Update local state
      setArticles(prev => prev.map(article => 
        article.id === articleId 
          ? { ...article, likes: article.likes + 1 } 
          : article
      ));
      
      showToast.system.success('Article liked!');
    } catch (err) {
      console.error('Error liking article:', err);
      showToast.system.error('Failed to like article');
    }
  };

  return {
    articles,
    loading,
    error,
    incrementViews,
    likeArticle
  };
}